import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import SearchSelect from '../../components/SearchSelect';
import {
    RoleInfo,
    SYSTEM_ROLES,
    UserGrantDetail,
    Vocabulary,
    allows,
    fetchUserGrant,
    fetchVocabulary,
    listRoles,
    setUserRoles,
} from '../../api/authz';
import { ApiError } from '../../api/request';

/**
 * 用户权限面板 (2026-09-11): 以人为中心看/改权限.
 *
 * 「权限分配」tab 是按绑定行铺开的流水, 一个人绑了几个角色就散成几行, 看不出"他到底能干什么";
 * 本面板一处看全: 准入身份 (隐式角色) → 显式绑定角色 (可增删, 一次保存) → 合并后的生效矩阵 (只读).
 * 入口: 管理员 / 普通用户列表每行「权限」, 权限分配列表点邮箱.
 */

interface Props {
    email: string | null;
    onClose: () => void;
    /** 保存成功后回调 (列表页回读绑定). */
    onChanged?: () => void;
}

const STATUS_ZH: Record<string, string> = {
    none: '未申请',
    pending: '待审批',
    approved: '已准入',
    rejected: '已拒绝',
};

/** 该用户的系统角色绑定 (admin / user), 面板只展示. */
const systemRolesOf = (d: UserGrantDetail) =>
    d.bindings.map((b) => b.roleName).filter((r) => SYSTEM_ROLES.includes(r));
/** 该用户可在面板增删的非系统角色. */
const customRolesOf = (d: UserGrantDetail) =>
    d.bindings.map((b) => b.roleName).filter((r) => !SYSTEM_ROLES.includes(r));

function UserPermissionModal({ email, onClose, onChanged }: Props) {
    const [detail, setDetail] = useState<UserGrantDetail | null>(null);
    const [vocab, setVocab] = useState<Vocabulary | null>(null);
    const [roles, setRoles] = useState<RoleInfo[]>([]);
    const [picked, setPicked] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // 词表与角色清单只随弹窗首次打开拉一次; 用户详情随 email 变化重拉.
    useEffect(() => {
        if (!email) return;
        if (!vocab)
            fetchVocabulary()
                .then(setVocab)
                .catch(() => setVocab(null));
        if (roles.length === 0)
            listRoles()
                .then((r) => setRoles(r.list || []))
                .catch(() => setRoles([]));
        setLoading(true);
        setError('');
        fetchUserGrant(email)
            .then((d) => {
                setDetail(d);
                setPicked(customRolesOf(d));
            })
            .catch((e) =>
                setError(
                    e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e),
                ),
            )
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email]);

    const roleLabel = (name: string) =>
        roles.find((r) => r.name === name)?.displayName ||
        detail?.roleLabels?.[name] ||
        name;

    // 脏检查只比**非系统**角色: picked 里从来不含 admin / user (它们由准入审批维护),
    // 拿全部绑定去比会让有系统角色的人永远显示"有未保存的改动"(2026-09-14 修).
    const dirty = useMemo(() => {
        if (!detail) return false;
        const cur = customRolesOf(detail).sort().join(',');
        return cur !== [...picked].sort().join(',');
    }, [detail, picked]);

    // 预览规则: 已保存的按后端合并结果; 有未保存改动时按 隐式角色 + 勾选角色 本地合并, 所见即将得.
    const previewRules = useMemo(() => {
        if (!detail) return [];
        if (!dirty) return detail.rules;
        const names = [
            ...systemRolesOf(detail),
            ...picked,
            ...(detail.groups || []).flatMap((g) => g.roles),
        ];
        return roles
            .filter((r) => names.includes(r.name))
            .flatMap((r) => r.rules);
    }, [detail, dirty, picked, roles]);

    const groups = useMemo(() => {
        if (!vocab) return [];
        const map = new Map<string, typeof vocab.resources>();
        vocab.resources.forEach((r) => {
            const g = r.group || '其他';
            map.set(g, [...(map.get(g) || []), r]);
        });
        return Array.from(map.entries());
    }, [vocab]);

    const onSave = async () => {
        if (!email) return;
        setBusy(true);
        setError('');
        try {
            const d = await setUserRoles(email, picked);
            setDetail(d);
            setPicked(customRolesOf(d));
            onChanged?.();
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={!!email}
            title="权限分配"
            subtitle={
                detail ? (
                    <span className="font-mono text-xs">
                        {detail.displayName ? `${detail.displayName} · ` : ''}
                        {detail.email}
                    </span>
                ) : (
                    email
                )
            }
            onClose={onClose}
            width={860}
        >
            {loading && <div className="text-sm text-slate-400">加载中…</div>}
            {error && (
                <div className="mb-3 rounded border border-rose-200 bg-rose-50 p-2 text-sm text-rose-600">
                    {error}
                </div>
            )}
            {detail && (
                <div className="space-y-5">
                    {/* 准入 + 系统角色 (在「管理员」「普通用户」tab 或角色成员面板里维护, 这里只看) */}
                    <section className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                        <span className="text-xs text-slate-500">准入</span>
                        <span
                            className={`tag ${detail.accessStatus === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
                        >
                            {STATUS_ZH[detail.accessStatus] ||
                                detail.accessStatus}
                        </span>
                        <span className="mx-1 text-slate-300">|</span>
                        <span className="text-xs text-slate-500">身份</span>
                        {systemRolesOf(detail).map((name) => (
                            <span
                                key={name}
                                className="tag bg-brand-50 text-brand-600"
                                title={name}
                            >
                                {roleLabel(name)}
                            </span>
                        ))}
                        {detail.accessStatus !== 'approved' && (
                            <span className="text-xs text-slate-400">
                                未准入 —— 角色要等准入通过后才生效
                            </span>
                        )}
                        {detail.accessStatus === 'approved' &&
                            systemRolesOf(detail).length === 0 && (
                                <span className="text-xs text-amber-600">
                                    已准入但无系统角色 (数据异常,
                                    请在人员管理里处理)
                                </span>
                            )}
                    </section>

                    {/* 所属用户组 (2026-09-13): 组带来的角色只展示, 进出组在「用户组」tab 维护 */}
                    {(detail.groups || []).length > 0 && (
                        <section className="rounded-lg border border-violet-100 bg-violet-50/40 px-3 py-2 text-sm">
                            <div className="mb-1 text-xs text-slate-500">
                                所属用户组
                                <span className="ml-1 text-slate-400">
                                    (组的角色随组生效, 要调整请到「用户组」tab)
                                </span>
                            </div>
                            <div className="space-y-1">
                                {(detail.groups || []).map((g) => (
                                    <div
                                        key={g.name}
                                        className="flex flex-wrap items-center gap-1.5"
                                    >
                                        <span
                                            className="tag bg-violet-50 text-violet-700"
                                            title={g.name}
                                        >
                                            {g.displayName || g.name}
                                        </span>
                                        <span className="text-xs text-slate-400">
                                            →
                                        </span>
                                        {g.roles.length === 0 && (
                                            <span className="text-xs text-slate-400">
                                                该组暂无角色
                                            </span>
                                        )}
                                        {g.roles.map((r) => (
                                            <span
                                                key={r}
                                                className="tag bg-slate-100 text-slate-600"
                                                title={r}
                                            >
                                                {roleLabel(r)}
                                            </span>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* 额外角色: 已选角色以标签呈现 (可摘), 搜索下拉追加 (2026-09-12) */}
                    <section>
                        <div className="mb-2 flex items-center justify-between">
                            <div>
                                <span className="text-sm font-medium text-slate-800">
                                    额外角色
                                </span>
                                <span className="ml-2 text-xs text-slate-400">
                                    资源管理员等; 改动后点保存生效
                                </span>
                            </div>
                            <div className="flex items-center gap-3">
                                {dirty && (
                                    <span className="text-xs text-amber-600">
                                        有未保存的改动
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className="btn-primary"
                                    disabled={!dirty || busy}
                                    onClick={onSave}
                                >
                                    {busy ? '保存中…' : '保存'}
                                </button>
                            </div>
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3">
                            <div className="flex flex-wrap items-center gap-2">
                                {picked.length === 0 && (
                                    <span className="text-xs text-slate-400">
                                        暂无额外角色 —— 在右侧搜索添加
                                    </span>
                                )}
                                {picked.map((name) => {
                                    const r = roles.find(
                                        (x) => x.name === name,
                                    );
                                    return (
                                        <span
                                            key={name}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 pl-3 pr-1.5 py-1 text-sm text-brand-700"
                                            title={name}
                                        >
                                            {r?.displayName || name}
                                            <button
                                                type="button"
                                                className="flex h-5 w-5 items-center justify-center rounded-full text-brand-400 hover:bg-brand-100 hover:text-rose-600"
                                                aria-label={`移除 ${r?.displayName || name}`}
                                                onClick={() =>
                                                    setPicked((p) =>
                                                        p.filter(
                                                            (x) => x !== name,
                                                        ),
                                                    )
                                                }
                                            >
                                                ×
                                            </button>
                                        </span>
                                    );
                                })}
                                <SearchSelect
                                    className="w-64"
                                    value=""
                                    placeholder="搜索并添加角色…"
                                    options={roles
                                        .filter(
                                            (r) =>
                                                !r.system &&
                                                !picked.includes(r.name),
                                        )
                                        .map((r) => ({
                                            value: r.name,
                                            label: `${r.displayName} (${r.name})`,
                                        }))}
                                    onChange={(v) => {
                                        if (v)
                                            setPicked((p) =>
                                                p.includes(v) ? p : [...p, v],
                                            );
                                    }}
                                />
                            </div>
                        </div>
                    </section>

                    {/* 生效矩阵 */}
                    <section>
                        <div className="mb-1 text-xs text-slate-500">
                            生效权限
                            {dirty ? ' (按当前勾选预览, 保存后生效)' : ''}
                        </div>
                        {!vocab ? (
                            <div className="text-xs text-slate-400">
                                词表加载中…
                            </div>
                        ) : (
                            <div className="max-h-[40vh] overflow-auto rounded border border-slate-200">
                                <table className="w-full text-xs">
                                    <thead className="sticky top-0 bg-slate-50">
                                        <tr>
                                            <th className="th text-left">
                                                资源
                                            </th>
                                            {vocab.verbs.map((v) => (
                                                <th
                                                    key={v.key}
                                                    className="th w-16 text-center"
                                                >
                                                    {v.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groups.map(([g, items]) => (
                                            <React.Fragment key={g}>
                                                <tr>
                                                    <td
                                                        className="td bg-slate-50/60 font-medium text-slate-500"
                                                        colSpan={
                                                            vocab.verbs.length +
                                                            1
                                                        }
                                                    >
                                                        {g}
                                                    </td>
                                                </tr>
                                                {items.map((r) => (
                                                    <tr key={r.key}>
                                                        <td className="td">
                                                            <span
                                                                className={
                                                                    r.sensitive
                                                                        ? 'text-rose-600'
                                                                        : ''
                                                                }
                                                            >
                                                                {r.label}
                                                            </span>
                                                            <span className="ml-1 font-mono text-slate-400">
                                                                {r.key}
                                                            </span>
                                                        </td>
                                                        {vocab.verbs.map(
                                                            (v) => {
                                                                const usable =
                                                                    !r.verbs ||
                                                                    r.verbs.includes(
                                                                        v.key,
                                                                    );
                                                                const on =
                                                                    usable &&
                                                                    allows(
                                                                        previewRules,
                                                                        r.key,
                                                                        v.key,
                                                                    );
                                                                return (
                                                                    <td
                                                                        key={
                                                                            v.key
                                                                        }
                                                                        className="td text-center"
                                                                    >
                                                                        {!usable ? (
                                                                            <span className="text-slate-200">
                                                                                —
                                                                            </span>
                                                                        ) : on ? (
                                                                            <span className="text-emerald-600">
                                                                                ✓
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-slate-200">
                                                                                ·
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                );
                                                            },
                                                        )}
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </div>
            )}
        </Modal>
    );
}

export default UserPermissionModal;
